import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../apiService';

const FEATURE_LABELS = {
  taxi:         { label: 'Taxi',           icon: '🚖' },
  delivery:     { label: 'Livraison',      icon: '🛵' },
  subscription: { label: 'Abonnement',     icon: '🎟️' },
  qr_scanner:   { label: 'Scanner QR',     icon: '📷' },
};

const PRICING_FIELDS = [
  { key: 'taxi_base_price',   label: 'Base taxi (CDF)' },
  { key: 'taxi_per_km',       label: 'Prix/km taxi (CDF)' },
  { key: 'moto_base_price',   label: 'Base moto (CDF)' },
  { key: 'moto_per_km',       label: 'Prix/km moto (CDF)' },
  { key: 'delivery_base_fee', label: 'Base livraison (CDF)' },
  { key: 'delivery_per_km',   label: 'Prix/km livraison (CDF)' },
];

const DEFAULT_NEW_CITY = {
  name: '', slug: '', center_lat: '', center_lng: '',
  radius_km: '50', is_active: true, tz_offset_hours: '',
  features: { taxi: true, delivery: true, subscription: false, qr_scanner: false },
};

export default function CitiesPage() {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editCity, setEditCity] = useState(null);          // city en cours d'édition
  const [editFeatures, setEditFeatures] = useState({});
  const [editPricing, setEditPricing] = useState({});
  const [editRadius, setEditRadius] = useState('');
  const [editTz, setEditTz] = useState('');               // '' = automatique (longitude)
  const [showCreate, setShowCreate] = useState(false);
  const [newCity, setNewCity] = useState(DEFAULT_NEW_CITY);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchCities = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.get('/admin/cities', { userType: 'admin' });
      if (res.success) setCities(res.cities);
    } catch (e) {
      setError('Erreur lors du chargement des villes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCities(); }, [fetchCities]);

  const openEdit = (city) => {
    setEditCity(city);
    setEditFeatures({ ...city.features });
    setEditPricing({ ...city.pricing_overrides });
    setEditRadius(String(city.radius_km || 50));
    setEditTz(city.tz_offset_hours == null ? '' : String(city.tz_offset_hours));
    setError('');
    setSuccess('');
  };

  const closeEdit = () => {
    setEditCity(null);
    setError('');
  };

  const handleToggleActive = async (city) => {
    if (city.is_main_city) return;
    try {
      await apiService.patch(`/admin/cities/${city.city_id}/toggle`, {}, { userType: 'admin' });
      fetchCities();
    } catch {
      setError('Erreur lors du changement de statut');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      // Convertir les champs pricing vides en null
      const cleanPricing = {};
      PRICING_FIELDS.forEach(({ key }) => {
        const val = editPricing[key];
        cleanPricing[key] = val !== '' && val !== null && val !== undefined ? Number(val) : null;
      });

      await apiService.put(`/admin/cities/${editCity.city_id}`, {
        features: editFeatures,
        pricing_overrides: cleanPricing,
        radius_km: Number(editRadius) || 50,
        tz_offset_hours: editTz === '' ? null : Number(editTz),
      }, { userType: 'admin' });
      setSuccess('Ville mise à jour avec succès');
      fetchCities();
      setTimeout(closeEdit, 800);
    } catch {
      setError('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Auto-générer le slug depuis le nom
  const handleNewCityName = (val) => {
    const slug = val.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    setNewCity(prev => ({ ...prev, name: val, slug }));
  };

  const handleCreate = async () => {
    setCreateError('');
    if (!newCity.name || !newCity.slug || newCity.center_lat === '' || newCity.center_lng === '') {
      setCreateError('Nom, slug, latitude et longitude sont obligatoires.');
      return;
    }
    setSaving(true);
    try {
      const res = await apiService.post('/admin/cities', {
        name: newCity.name,
        slug: newCity.slug,
        center_lat: parseFloat(newCity.center_lat),
        center_lng: parseFloat(newCity.center_lng),
        radius_km: Number(newCity.radius_km) || 50,
        tz_offset_hours: newCity.tz_offset_hours === '' ? null : Number(newCity.tz_offset_hours),
        is_active: newCity.is_active,
        features: newCity.features,
      }, { userType: 'admin' });
      if (res.success) {
        setCreateSuccess(`Ville "${res.city.name}" créée avec succès !`);
        fetchCities();
        setTimeout(() => { setShowCreate(false); setNewCity(DEFAULT_NEW_CITY); setCreateSuccess(''); }, 1200);
      }
    } catch (e) {
      setCreateError(e.message || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header"><h1 className="page-title">🌍 Villes</h1></div>
        <div style={{ padding: 40, textAlign: 'center', color: '#5C636E' }}>Chargement…</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h1 className="page-title">🌍 Villes</h1>
          <p style={{ color: '#5C636E', marginTop: 4 }}>
            Gérez les villes actives, leurs fonctionnalités et leur tarification.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => { setShowCreate(true); setCreateError(''); setCreateSuccess(''); }}>
          ➕ Ajouter une ville
        </button>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Ville</th>
              <th>Slug</th>
              <th>Rayon GPS</th>
              <th>Fuseau</th>
              <th>Statut</th>
              <th>Fonctionnalités</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {cities.map(city => (
              <tr key={city.city_id}>
                <td>
                  <strong>{city.name}</strong>
                  {city.is_main_city && (
                    <span style={{
                      marginLeft: 8, fontSize: 11, background: '#FEF3C7',
                      color: '#C9350F', padding: '2px 8px', borderRadius: 10
                    }}>Principal</span>
                  )}
                </td>
                <td><code style={{ background: '#F4F5F7', padding: '2px 6px', borderRadius: 4 }}>{city.slug}</code></td>
                <td>{city.radius_km} km</td>
                <td title="Heures de nuit en heure locale de la ville">UTC+{city.tz_offset_hours ?? (city.center_lng < 22 ? 1 : 2)}{city.tz_offset_hours == null ? <span style={{ color: '#9AA0A8', fontSize: 11 }}> (auto)</span> : null}</td>
                <td>
                  <span style={{
                    display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 12,
                    fontWeight: 600,
                    background: city.is_active ? '#D1FAE5' : '#FEE2E2',
                    color: city.is_active ? '#065F46' : '#991B1B',
                  }}>
                    {city.is_active ? '✅ Active' : '❌ Inactive'}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {Object.entries(FEATURE_LABELS).map(([key, { label, icon }]) => (
                      <span key={key} style={{
                        fontSize: 12, padding: '2px 8px', borderRadius: 10,
                        background: city.features?.[key] ? '#D1FAE5' : '#F4F5F7',
                        color: city.features?.[key] ? '#065F46' : '#9AA0A8',
                      }}>
                        {icon} {label}
                      </span>
                    ))}
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => openEdit(city)}>
                      ✏️ Modifier
                    </button>
                    {!city.is_main_city && (
                      <button
                        className={`btn btn-sm ${city.is_active ? 'btn-danger' : 'btn-success'}`}
                        onClick={() => handleToggleActive(city)}
                      >
                        {city.is_active ? 'Désactiver' : 'Activer'}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Modal création ── */}
      {showCreate && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#FFF', borderRadius: 16, padding: 32, width: 560,
            maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <h2 style={{ marginBottom: 20 }}>➕ Ajouter une ville</h2>

            {createError && <div className="alert alert-error" style={{ marginBottom: 12 }}>{createError}</div>}
            {createSuccess && <div className="alert alert-success" style={{ marginBottom: 12 }}>{createSuccess}</div>}

            {/* Nom + Slug */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>Nom de la ville *</label>
                <input
                  className="form-input"
                  placeholder="ex: Uvira"
                  value={newCity.name}
                  onChange={e => handleNewCityName(e.target.value)}
                />
              </div>
              <div>
                <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>Slug *</label>
                <input
                  className="form-input"
                  placeholder="ex: uvira"
                  value={newCity.slug}
                  onChange={e => setNewCity(prev => ({ ...prev, slug: e.target.value }))}
                />
              </div>
            </div>

            {/* Coordonnées GPS */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>Latitude *</label>
                <input
                  className="form-input" type="number" step="any"
                  placeholder="-3.395"
                  value={newCity.center_lat}
                  onChange={e => setNewCity(prev => ({ ...prev, center_lat: e.target.value }))}
                />
              </div>
              <div>
                <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>Longitude *</label>
                <input
                  className="form-input" type="number" step="any"
                  placeholder="29.153"
                  value={newCity.center_lng}
                  onChange={e => setNewCity(prev => ({ ...prev, center_lng: e.target.value }))}
                />
              </div>
              <div>
                <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>Rayon GPS (km)</label>
                <input
                  className="form-input" type="number" min={1} max={200}
                  value={newCity.radius_km}
                  onChange={e => setNewCity(prev => ({ ...prev, radius_km: e.target.value }))}
                />
              </div>
              <div>
                <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>Fuseau horaire (heures de nuit)</label>
                <select className="form-input" value={newCity.tz_offset_hours} onChange={e => setNewCity(prev => ({ ...prev, tz_offset_hours: e.target.value }))}>
                  {[['', 'Automatique (longitude)'], ['1', 'UTC+1 — Kinshasa, Kongo Central, Équateur…'], ['2', 'UTC+2 — Kivu, Katanga, Tshopo, Kasaï…']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>

            {/* Fonctionnalités */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontWeight: 600, display: 'block', marginBottom: 10 }}>🛠 Fonctionnalités</label>
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                {Object.entries(FEATURE_LABELS).map(([key, { label, icon }]) => (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!newCity.features[key]}
                      onChange={e => setNewCity(prev => ({ ...prev, features: { ...prev.features, [key]: e.target.checked } }))}
                    />
                    <span>{icon} {label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Statut */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={newCity.is_active}
                  onChange={e => setNewCity(prev => ({ ...prev, is_active: e.target.checked }))}
                />
                <span style={{ fontWeight: 600 }}>Ville active immédiatement</span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => { setShowCreate(false); setNewCity(DEFAULT_NEW_CITY); }} disabled={saving}>Annuler</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>
                {saving ? 'Création…' : '➕ Créer la ville'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal d'édition ── */}
      {editCity && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#FFF', borderRadius: 16, padding: 32, width: 520,
            maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }}>
            <h2 style={{ marginBottom: 4 }}>✏️ Modifier — {editCity.name}</h2>
            <p style={{ color: '#5C636E', marginBottom: 20, fontSize: 14 }}>
              slug: <code>{editCity.slug}</code>
            </p>

            {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}
            {success && <div className="alert alert-success" style={{ marginBottom: 12 }}>{success}</div>}

            {/* Rayon GPS */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>
                📍 Rayon de détection GPS (km)
              </label>
              <input
                type="number"
                className="form-input"
                value={editRadius}
                onChange={e => setEditRadius(e.target.value)}
                min={1}
                max={200}
                style={{ width: 120 }}
              />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>🌙 Fuseau horaire (heures de nuit en heure locale)</label>
              <select className="form-input" value={editTz} onChange={e => setEditTz(e.target.value)} style={{ maxWidth: 360 }}>
                {[['', 'Automatique (longitude)'], ['1', 'UTC+1 — Kinshasa, Kongo Central, Équateur…'], ['2', 'UTC+2 — Kivu, Katanga, Tshopo, Kasaï…']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <p style={{ fontSize: 12, color: '#9AA0A8', margin: '6px 0 0' }}>Automatique = UTC+1 à l'ouest de 22° E (Kinshasa, Matadi), UTC+2 à l'est (Bukavu, Goma, Lubumbashi, Kisangani…).</p>
            </div>

            {/* Fonctionnalités */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontWeight: 600, display: 'block', marginBottom: 10 }}>
                🛠 Fonctionnalités
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {Object.entries(FEATURE_LABELS).map(([key, { label, icon }]) => (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!editFeatures[key]}
                      onChange={e => setEditFeatures(prev => ({ ...prev, [key]: e.target.checked }))}
                      disabled={editCity.is_main_city && (key === 'taxi' || key === 'delivery')}
                    />
                    <span>{icon} {label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Tarification spécifique */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>
                💰 Tarification spécifique <span style={{ fontWeight: 400, fontSize: 12, color: '#5C636E' }}>(vide = tarif global)</span>
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {PRICING_FIELDS.map(({ key, label }) => (
                  <div key={key}>
                    <label style={{ fontSize: 12, color: '#414852', display: 'block', marginBottom: 4 }}>{label}</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="global"
                      value={editPricing[key] ?? ''}
                      onChange={e => setEditPricing(prev => ({ ...prev, [key]: e.target.value }))}
                      min={0}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={closeEdit} disabled={saving}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Sauvegarde…' : '💾 Sauvegarder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
